import React from 'react';
import type { ReadonlyWatchable } from '@cotera/client/v0/actions/framework';
import type { DataGridViewModel } from './view-model';

export type DataGridRowId = string | number;

export type DataGridPinnedSide = 'left' | 'right';

export type DataGridSortDirection = 'asc' | 'desc';

export type DataGridSort = {
  columnId: string;
  direction: DataGridSortDirection;
};

/**
 * Scalar values a structured filter can carry. Kept deliberately narrow so
 * warehouse adapters can translate them into literals without guessing.
 */
export type DataGridFilterScalar = string | number | boolean | null;

export type DataGridStructuredFilterValue =
  | { kind: 'in'; values: DataGridFilterScalar[] }
  | {
      kind: 'between';
      min: number | string;
      max: number | string;
      inclusiveMax?: boolean;
    };

/**
 * Comparisons the header's filter form can build. `isNull` / `isNotNull`
 * ignore the operand; the rest read it as the column's own type.
 */
export type DataGridFilterComparison =
  | 'contains'
  | 'equals'
  | 'notEquals'
  | 'greaterThan'
  | 'greaterThanOrEqual'
  | 'lessThan'
  | 'lessThanOrEqual'
  | 'isNull'
  | 'isNotNull';

export type DataGridComparisonFilterValue = {
  kind: 'compare';
  comparison: DataGridFilterComparison;
  value: DataGridFilterScalar;
};

/**
 * Everything a filter's `value` can be. Bare scalars predate the structured
 * forms and still mean "substring match", so old callers keep working.
 */
export type DataGridFilterValue =
  | DataGridStructuredFilterValue
  | DataGridComparisonFilterValue
  | DataGridFilterScalar;

export type DataGridFilter = {
  columnId: string;
  value: unknown;
};

export type DataGridCellAddress = {
  rowId: DataGridRowId;
  rowIndex: number;
  columnId: string;
  columnIndex: number;
};

export type DataGridCellRange = {
  start: DataGridCellAddress;
  end: DataGridCellAddress;
};

/**
 * Logical column types the grid knows how to decorate with an icon and a
 * default stats presentation. Callers may map their own domain types onto
 * these without adopting the grid's vocabulary wholesale.
 */
export type DataGridColumnDataType =
  | 'text'
  | 'number'
  | 'boolean'
  | 'date'
  | 'timestamp'
  | 'category'
  | 'unknown';

export type DataGridNumberFormat = 'number' | 'percent' | 'compact';

export type DataGridColumnAlignment = 'left' | 'center' | 'right';

/**
 * Where focus goes when an edit is committed from the keyboard: down for Enter,
 * right for Tab, the reverse of each with Shift, and nowhere for a commit that
 * came from a blur or a picker.
 */
export type DataGridEditAdvance = 'up' | 'down' | 'left' | 'right' | null;

export type DataGridColumnDisplayOptions = {
  numberFormat: DataGridNumberFormat;
  /** null keeps the raw value's own precision. */
  decimals: number | null;
  alignment: DataGridColumnAlignment;
  inCellBar: boolean;
  /**
   * Upper bound for in-cell bars. Percent-formatted columns default to 1;
   * other columns need this set (typically from the column's numeric stats)
   * before a bar can be drawn.
   */
  barMax?: number;
};

/**
 * How much vertical room every row gets. A grid-wide choice rather than a
 * per-column one — rows are shared — kept as a named size instead of a pixel
 * count so the value survives a change of taste about what "tall" means.
 */
export type DataGridRowHeight = 'compact' | 'standard' | 'tall';

/**
 * Pixels per named size. `tall` is deliberately more than double `compact`:
 * its point is fitting the wrapped lines a truncated cell was hiding, and a
 * height that only fits one and a half of them buys nothing.
 */
export const DATA_GRID_ROW_HEIGHTS: Record<DataGridRowHeight, number> = {
  compact: 28,
  standard: 36,
  tall: 76,
};

export const DEFAULT_DATA_GRID_ROW_HEIGHT: DataGridRowHeight = 'standard';

/**
 * The sizes as offered to a user, in order. One list, because the choice is
 * presented in two places — a column's display options and the host's own
 * settings menu — and two lists is how they end up disagreeing about what the
 * middle one is called.
 */
export const DATA_GRID_ROW_HEIGHT_OPTIONS: readonly {
  value: DataGridRowHeight;
  label: string;
}[] = [
  { value: 'compact', label: 'Compact' },
  { value: 'standard', label: 'Standard' },
  { value: 'tall', label: 'Tall' },
];

export const isDataGridRowHeight = (
  value: unknown
): value is DataGridRowHeight =>
  value === 'compact' || value === 'standard' || value === 'tall';

export const DEFAULT_DATA_GRID_DISPLAY_OPTIONS: DataGridColumnDisplayOptions = {
  numberFormat: 'number',
  decimals: null,
  alignment: 'left',
  inCellBar: false,
};

/**
 * One mark in a stats chart. `filter` is what gets applied when the user
 * clicks the mark, so stat producers stay in control of filter semantics.
 */
export type DataGridStatBucket = {
  key: string;
  label: string;
  count: number;
  share?: number;
  filter?: DataGridStructuredFilterValue;
};

export type DataGridColumnStats =
  | { kind: 'loading' }
  | { kind: 'error'; message?: string }
  | { kind: 'summary'; label: React.ReactNode; value: React.ReactNode }
  | { kind: 'custom'; content: React.ReactNode }
  | {
      kind: 'textSummary';
      uniqueCount: number;
      nullCount: number;
      totalCount: number;
      samples?: string[];
    }
  | {
      kind: 'categorical';
      buckets: DataGridStatBucket[];
      /** Distinct values in the column, not just the ones in `buckets`. */
      uniqueCount?: number;
      nullCount?: number;
      otherCount?: number;
    }
  | {
      kind: 'numericDistribution';
      buckets: DataGridStatBucket[];
      min: number;
      max: number;
      mean: number;
      nullCount?: number;
    }
  | {
      kind: 'temporalDistribution';
      buckets: DataGridStatBucket[];
      min: string;
      max: string;
      nullCount?: number;
    };

/** Values a cell editor can produce. */
export type DataGridCellValue = string | number | boolean | null;

export type DataGridEditorKind = 'text' | 'number' | 'date' | 'select';

export type DataGridCellEdit = {
  rowId: DataGridRowId;
  columnId: string;
  /** The value before the *first* edit, so reverting clears the dirty flag. */
  previousValue: unknown;
  value: DataGridCellValue;
};

export const dataGridEditKey = (
  rowId: DataGridRowId,
  columnId: string
): string => `${String(rowId)}::${columnId}`;

export type DataGridColumn<TRow, TValue = unknown, TMeta = unknown> = {
  id: string;
  header: React.ReactNode;
  /**
   * Plain-text name for surfaces that print the column rather than render
   * `header` (the expand-cell modal's title). Needed when `header` is rich
   * markup; otherwise a string `header` is used, then `id`.
   */
  headerText?: string;
  type?: DataGridColumnDataType;
  typeLabel?: string;
  /** Opt in to in-place editing. Ignored when `computed` is true. */
  editable?: boolean;
  /** Derived column: marked `fx` in the header and never editable. */
  computed?: boolean;
  /** Defaults to an editor inferred from `type`. */
  editorKind?: DataGridEditorKind;
  /** Choices for a `select` editor, resolved when the editor opens. */
  getEditOptions?: () => readonly string[] | undefined;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  pinned?: DataGridPinnedSide | null;
  visible?: boolean;
  sortable?: boolean;
  filterable?: boolean;
  meta?: TMeta;
  getValue: (row: TRow) => TValue;
  renderCell?: (
    context: DataGridCellContext<TRow, TValue, TMeta>
  ) => React.ReactNode;
  renderHeader?: (
    context: DataGridHeaderContext<TRow, TValue, TMeta>
  ) => React.ReactNode;
  renderHeaderStats?: (
    context: DataGridHeaderContext<TRow, TValue, TMeta>
  ) => React.ReactNode;
  renderDetail?: (
    context: DataGridCellContext<TRow, TValue, TMeta>
  ) => React.ReactNode;
};

export type DataGridHeaderContext<TRow, TValue = unknown, TMeta = unknown> = {
  column: DataGridColumn<TRow, TValue, TMeta>;
  columnIndex: number;
  sort: DataGridSort | null;
  sortIndex: number | null;
  sortCount: number;
  filter: DataGridFilter | null;
  stats: DataGridColumnStats | null;
  statsExpanded: boolean;
  displayOptions: DataGridColumnDisplayOptions;
  /** Grid-wide, offered here because the display dialog is where it is looked for. */
  rowHeight: DataGridRowHeight;
  commands: {
    resize(width: number): void;
    reorderBefore(sourceColumnId: string): void;
    pin(side: DataGridPinnedSide): void;
    unpin(): void;
    setSort(direction: DataGridSortDirection | null): void;
    toggleSort(direction: DataGridSortDirection): void;
    setFilter(value: unknown): void;
    clearFilter(): void;
    /** Toggle a structured stat mark filter (categorical / range buckets). */
    toggleStatFilter(value: DataGridStructuredFilterValue): void;
    /** Ask the host to load this column's stats; no-op while collapsed. */
    requestStatsLoad(): void;
    setDisplayOptions(options: Partial<DataGridColumnDisplayOptions>): void;
    /** Changes every row, not just this column's cells. */
    setRowHeight(rowHeight: DataGridRowHeight): void;
    hide(): void;
  };
};

export type DataGridCellContext<TRow, TValue = unknown, TMeta = unknown> = {
  row: TRow;
  value: TValue;
  column: DataGridColumn<TRow, TValue, TMeta>;
  address: DataGridCellAddress;
  displayOptions: DataGridColumnDisplayOptions;
  state: {
    focused: boolean;
    selected: boolean;
    rowSelected: boolean;
    cellSelected: boolean;
    hovered: boolean;
    visible: boolean;
    editable: boolean;
    editing: boolean;
    /**
     * The character that opened the editor, when typing did. Null whenever the
     * edit was opened any other way, and the editor then starts from the cell's
     * own value.
     */
    editSeed: string | null;
    /** Has an uncommitted edit relative to the loaded value. */
    dirty: boolean;
  };
  anchorRef: React.MutableRefObject<HTMLElement | null>;
  commands: {
    focus(): void;
    select(): void;
    /**
     * What an unmodified click on a cell means: pick this cell alone, or drop
     * the selection when it is already the only one picked. Clearing takes the
     * focus ring with it, so the cell ends up unmarked rather than half-marked.
     */
    toggleSelectOnly(): void;
    toggleSelect(): void;
    selectRange(): void;
    openOverlay(content: React.ReactNode): void;
    closeOverlay(): void;
    openDetail(): void;
    /**
     * Opens the built-in cell-detail modal — the full value with room to read
     * it, and an editor when the cell is editable. Gated for display by
     * {@link DataGridProps.enableCellDetail}, but always callable.
     */
    expandCell(): void;
    /** `seed` replaces the cell's value in the editor; see `state.editSeed`. */
    beginEdit(seed?: string): void;
    commitEdit(value: DataGridCellValue, advance?: DataGridEditAdvance): void;
    cancelEdit(): void;
  };
};

export type DataGridColumnLayoutItem<TRow> = {
  column: DataGridColumn<TRow>;
  x: number;
  width: number;
  columnIndex: number;
  pinned: DataGridPinnedSide | null;
};

export type DataGridRowNumberLayoutItem = {
  id: '__row_number__';
  x: number;
  width: number;
  columnIndex: number;
  pinned: 'left';
};

export type DataGridColumnLayout<TRow> = {
  rowNumber: DataGridRowNumberLayoutItem;
  leftPinned: DataGridColumnLayoutItem<TRow>[];
  regular: DataGridColumnLayoutItem<TRow>[];
  rightPinned: DataGridColumnLayoutItem<TRow>[];
  leftPinnedWidth: number;
  regularWidth: number;
  rightPinnedWidth: number;
  totalWidth: number;
  visibleColumns: DataGridColumnLayoutItem<TRow>[];
  ariaColumnCount: number;
};

export type DataGridVirtualizationResult = {
  startIndex: number;
  endIndex: number;
  visibleCount: number;
  offsetY: number;
  totalHeight: number;
};

export type DataGridColumnVirtualizationResult = {
  startIndex: number;
  endIndex: number;
  visibleCount: number;
  offsetX: number;
  totalWidth: number;
};

export type DataGridVisibleWindow = {
  startRowIndex: number;
  endRowIndex: number;
  startColumnIndex: number;
  endColumnIndex: number;
};

/**
 * Incremental updates for live datasets. Applying a patch touches only the
 * affected rows so the grid does not have to swap the whole rows array.
 */
export type DataGridRowPatch<TRow> =
  | {
      type: 'update-cell';
      rowId: DataGridRowId;
      columnId: string;
      value: unknown;
    }
  | { type: 'update-row'; rowId: DataGridRowId; row: TRow }
  | { type: 'insert-row'; row: TRow; atIndex?: number }
  | { type: 'delete-row'; rowId: DataGridRowId }
  | {
      type: 'splice-rows';
      startIndex: number;
      deleteCount: number;
      rows: TRow[];
    };

export type DataGridRowSource<TRow> = {
  rows: ReadonlyWatchable<TRow[]>;
  totalRows: ReadonlyWatchable<number | null>;
  hasMore: ReadonlyWatchable<boolean>;
  isLoading: ReadonlyWatchable<boolean>;
  loadMore: () => void | Promise<void>;
  loadWindow?: (window: DataGridVisibleWindow) => void | Promise<void>;
  /** Present on patchable sources; lets callers push granular live updates. */
  applyPatch?: (patch: DataGridRowPatch<TRow>) => void;
};

export type DataGridColumnStatsSource = {
  get(columnId: string): ReadonlyWatchable<DataGridColumnStats | undefined>;
};

export type DataGridCellComponentProps<TRow> = {
  context: DataGridCellContext<TRow>;
  style: React.CSSProperties;
  onClick?: (event: React.MouseEvent) => void;
};

export type DataGridHeaderComponentProps<TRow> = {
  context: DataGridHeaderContext<TRow>;
  style: React.CSSProperties;
  /** Host controls for this column, from {@link DataGridProps.renderColumnHeaderActions}. */
  actions?: React.ReactNode;
  /** Extra dropdown-menu items, from {@link DataGridProps.renderColumnMenuItems}. */
  menuItems?: React.ReactNode;
};

export type DataGridRowNumberContext<TRow> = {
  rowId: DataGridRowId;
  rowIndex: number;
  row: TRow;
};

export type DataGridRowComponentProps<TRow> = {
  row: TRow;
  rowId: DataGridRowId;
  rowIndex: number;
  selected: boolean;
  children: React.ReactNode;
  style: React.CSSProperties;
  onClick: (event: React.MouseEvent) => void;
};

export type DataGridRowDetailContext<TRow> = {
  row: TRow;
  rowId: DataGridRowId;
  rowIndex: number;
  /** Collapses this row, for a close affordance inside the panel. */
  collapse: () => void;
};

export type DataGridEmptyStateProps = {
  /** Column filters currently narrowing the grid. */
  filterCount: number;
  clearFilters: () => void;
};

export type DataGridTopBarProps<TRow> = {
  rows: TRow[];
  columns: DataGridColumn<TRow>[];
  selectedRowIds: Set<DataGridRowId>;
  selectedCellRanges: DataGridCellRange[];
  totalRows: number | null;
  totalLoadedRows: number;
  sorts: DataGridSort[];
  filters: DataGridFilter[];
  isLoading: boolean;
  statsExpanded: boolean;
  setStatsExpanded: (expanded: boolean) => void;
  /**
   * The grid-wide row size, offered here as well as in a column's display
   * options: it belongs to the whole grid, and a setting reachable only from
   * one column's dropdown is a setting most people never find.
   */
  rowHeight: DataGridRowHeight;
  setRowHeight: (rowHeight: DataGridRowHeight) => void;
  clearSort: (columnId: string) => void;
  clearFilter: (columnId: string) => void;
  /** Columns with `visible: false`; the top bar is how they get restored. */
  hiddenColumns: DataGridColumn<TRow>[];
  setColumnVisible: (columnId: string, visible: boolean) => void;
};

export type DataGridFooterProps<TRow> = {
  rows: TRow[];
  columns: DataGridColumn<TRow>[];
  totalRows: number | null;
  totalLoadedRows: number;
  sorts: DataGridSort[];
  filters: DataGridFilter[];
  selectedRowIds: Set<DataGridRowId>;
  selectedCellRanges: DataGridCellRange[];
  isLoading: boolean;
  clearSort: (columnId: string) => void;
  clearFilter: (columnId: string) => void;
  clearFilters: () => void;
  /**
   * The focused cell's expand affordance, present only while
   * {@link DataGridProps.enableCellDetail} is on and a cell is focused. Render
   * it as a chip in the footer's chip gutter, beside the sort/filter chips.
   */
  expandableCell?: {
    label: string;
    editable: boolean;
    onExpand: () => void;
  } | null;
  /**
   * The copy affordance for whatever is selected, absent when nothing is.
   * Rendered as a chip beside {@link expandableCell}: Ctrl/Cmd+C does the same
   * thing, and a shortcut with nothing on screen to name it is a feature only
   * the people who wrote it know about.
   */
  copyableSelection?: {
    /** "cells" or "rows", for the chip's label. */
    unit: 'cells' | 'rows';
    count: number;
    onCopy: () => void;
  } | null;
};

export type DataGridProps<TRow> = {
  rows?: ReadonlyWatchable<TRow[]>;
  rowSource?: DataGridRowSource<TRow>;
  getRowId: (row: TRow) => DataGridRowId;
  viewModel: DataGridViewModel<TRow>;
  className?: string;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  columnStats?: ReadonlyWatchable<
    Record<string, DataGridColumnStats | undefined>
  >;
  /**
   * Per-column stats watchables. Preferred for live data: each rendered header
   * subscribes only to its own column so a stats delta never re-renders the
   * whole header row.
   */
  columnStatsSource?: DataGridColumnStatsSource;
  onLoadMore?: () => void | Promise<void>;
  onVisibleWindowChange?: (window: DataGridVisibleWindow) => void;
  onSortChange?: (sorts: DataGridSort[]) => void;
  onFilterChange?: (filters: DataGridFilter[]) => void;
  onHeaderStatsVisible?: (columnId: string) => void;
  /**
   * Controls rendered in a column's header, right of its title — for an action
   * that belongs to one column rather than to the grid (e.g. a "run these rows"
   * menu on the column that reports the runs). Called per rendered header;
   * return null for the columns it does not own.
   *
   * A grid-level hook rather than a field on the column because the state such
   * a control acts on is rarely in scope where columns are declared.
   */
  renderColumnHeaderActions?: (
    context: DataGridHeaderContext<TRow>
  ) => React.ReactNode;
  /**
   * Extra items appended to each column's header dropdown menu, below the
   * built-in sort/pin/hide entries. Use for column-scoped actions such as
   * rename or remove.
   */
  renderColumnMenuItems?: (
    context: DataGridHeaderContext<TRow>
  ) => React.ReactNode;
  /** Fired once per committed edit, including edits that revert to original. */
  onCellEdit?: (edit: DataGridCellEdit) => void;
  /** Persist pending edits. Pending state is cleared only when this resolves. */
  onSaveEdits?: (edits: DataGridCellEdit[]) => void | Promise<void>;
  onRevertEdits?: (edits: DataGridCellEdit[]) => void | Promise<void>;
  /**
   * Persist each edit through `onSaveEdits` the moment it is committed rather
   * than collecting edits behind a Save/Revert banner. In this mode no banner
   * is shown, cells never linger as dirty, and `onRevertEdits` is unused.
   */
  autoSaveEdits?: boolean;
  /**
   * Turns on the expand-cell affordances: a per-cell expand button, a
   * focused-cell indicator above the footer, and a detail modal (view, or edit
   * when the cell is editable). Off by default so existing grids are unchanged.
   */
  enableCellDetail?: boolean;
  onSelectCell?: (context: DataGridCellContext<TRow>) => void;
  onCellSelectionChange?: (ranges: DataGridCellRange[]) => void;
  onRowSelectionChange?: (rowIds: Set<DataGridRowId>) => void;
  /**
   * Inline detail panel for an expanded row. Supplying it is what turns on the
   * expand gutter; its height comes from the view model's `expansionHeight`,
   * which must be set for the panel to have any room.
   */
  /**
   * Replaces the built-in body empty state.
   *
   * Handed the filter count and the way to clear them, so a host that wants its
   * own wording does not have to rebuild the one affordance that matters.
   *
   * The grid can only ever name its own filters as the cause. A host narrowing
   * by something of its own knows a reason the grid does not, and an empty
   * table that cannot say why it is empty reads as a broken one.
   */
  renderEmpty?: (input: DataGridEmptyStateProps) => React.ReactNode;
  renderRowDetail?: (
    context: DataGridRowDetailContext<TRow>
  ) => React.ReactNode;
  /**
   * Replaces the row-number gutter's default `rowIndex + 1` content. The gutter
   * cell carries a `group/gutter` marker, so an overlay (e.g. a delete control)
   * can reveal itself on hover with `group-hover/gutter:*`.
   */
  renderRowNumber?: (
    context: DataGridRowNumberContext<TRow>
  ) => React.ReactNode;
  /**
   * Shows the column-stats disclosure chevron in the row-number header. Turn it
   * off for grids that never load stats. Defaults to true.
   */
  showStatsToggle?: boolean;
  CellComponent?: React.ComponentType<DataGridCellComponentProps<TRow>>;
  HeaderComponent?: React.ComponentType<DataGridHeaderComponentProps<TRow>>;
  RowComponent?: React.ComponentType<DataGridRowComponentProps<TRow>>;
  TopBarComponent?: React.ComponentType<DataGridTopBarProps<TRow>>;
  FooterComponent?: React.ComponentType<DataGridFooterProps<TRow>>;
};

export function propertyColumn<TRow, TKey extends keyof TRow, TMeta = unknown>(
  definition: Omit<
    DataGridColumn<TRow, TRow[TKey], TMeta>,
    'id' | 'getValue'
  > & {
    key: TKey;
    id?: string;
  }
): DataGridColumn<TRow, TRow[TKey], TMeta> {
  const { key, id, ...rest } = definition;
  return {
    ...rest,
    id: id ?? String(key),
    getValue: (row) => row[key],
  };
}
